import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Copy, Archive, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import api, { formatPrice, mediaUrl, formatApiError } from "../../lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../../components/ui/alert-dialog";

const badge = (p) => {
  const s = p.availability_status;
  const map = { available: "bg-emeraldauth/10 text-emeraldauth", sold: "bg-pitch text-ivory", reserved: "bg-champagne-soft text-champagne-dark", archived: "bg-stonemuted/20 text-stonemuted" };
  return map[s] || "bg-stonemuted/20 text-stonemuted";
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async (q = "") => {
    setLoading(true);
    try { const { data } = await api.get("/admin/products", { params: q ? { search: q } : {} }); setProducts(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setTimeout(() => load(search), 300); return () => clearTimeout(t); }, [search]);

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(search); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif text-3xl text-pitch">Products</h1>
        <Link to="/admin/products/new" data-testid="admin-add-product"
          className="inline-flex items-center gap-2 bg-pitch text-ivory text-xs font-mono uppercase tracking-[0.15em] px-6 py-3 hover:bg-charcoal transition-colors">
          <Plus className="w-4 h-4" /> Add Product
        </Link>
      </div>

      <div className="relative mb-6 max-w-sm">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stonemuted" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products…"
          data-testid="admin-product-search" className="w-full border border-linen bg-white pl-10 pr-4 py-2.5 text-sm outline-none focus:border-pitch" />
      </div>

      <div className="bg-white border border-linen overflow-x-auto" data-testid="admin-inventory-table">
        <table className="w-full text-sm min-w-[720px]">
          <thead><tr className="text-left text-stonemuted border-b border-linen">
            {["", "Product", "SKU", "Price", "Inv.", "Status", "Actions"].map((h, i) => <th key={i} className="px-4 py-3 font-mono text-xs uppercase tracking-wider">{h}</th>)}
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={7} className="px-4 py-12 text-center text-stonemuted">Loading…</td></tr>
              : products.length === 0 ? <tr><td colSpan={7} className="px-4 py-12 text-center text-stonemuted">No products found.</td></tr>
              : products.map((p) => (
                <tr key={p.id} className="border-b border-linen/60 hover:bg-bone/50" data-testid="admin-product-row">
                  <td className="px-4 py-2"><div className="w-11 h-14 bg-bone overflow-hidden">{p.images?.[0] && <img src={mediaUrl(p.images[0])} alt="" className="w-full h-full object-cover" />}</div></td>
                  <td className="px-4 py-2">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-stonemuted">{p.brand}</div>
                    <button onClick={() => navigate(`/admin/products/${p.id}`)} className="font-serif text-base text-pitch hover:text-champagne-dark text-left">{p.title}</button>
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">{p.sku}</td>
                  <td className="px-4 py-2">{formatPrice(p.price)}</td>
                  <td className="px-4 py-2">{p.inventory_quantity}</td>
                  <td className="px-4 py-2"><span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 ${badge(p)}`}>{p.availability_status}</span></td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-1">
                      <button title="Duplicate" onClick={() => act(() => api.post(`/admin/products/${p.id}/duplicate`), "Product duplicated")} className="p-2 hover:text-champagne-dark"><Copy className="w-4 h-4" /></button>
                      {p.availability_status !== "sold" && <button title="Mark Sold" onClick={() => act(() => api.post(`/admin/products/${p.id}/mark-sold`), "Marked as sold")} className="p-2 hover:text-pitch text-[10px] font-mono uppercase">Sold</button>}
                      <button title="Archive" onClick={() => act(() => api.post(`/admin/products/${p.id}/archive`), "Product archived")} className="p-2 hover:text-champagne-dark"><Archive className="w-4 h-4" /></button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild><button title="Delete" className="p-2 hover:text-vermilion" data-testid="admin-delete-product"><Trash2 className="w-4 h-4" /></button></AlertDialogTrigger>
                        <AlertDialogContent className="bg-ivory">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="font-serif text-2xl">Delete this product?</AlertDialogTitle>
                            <AlertDialogDescription>This permanently removes “{p.title}”. This action cannot be undone. Consider archiving instead.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction className="bg-vermilion hover:bg-vermilion/90" onClick={() => act(() => api.delete(`/admin/products/${p.id}`), "Product deleted")}>Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
